import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ownLocations } from "@/lib/scope";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const ROLE_LABELS = { user: "Gebruiker", admin: "Beheerder", lezer: "Lezer" };
// Same tooltip copy as Beheer → Gebruikers role select (kept in sync intentionally).
const ROLE_HINTS = {
  user: "Kan opnemen, verwerken en de eigen kluis beheren.",
  admin: "Volledige toegang — alle memo's, gebruikers en instellingen beheren.",
  lezer: "Kan alleen de Bibliotheek lezen — niet inspreken of beheren.",
};

function ProfileField({ label, value, testId }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase mb-1">{label}</div>
      <div className="text-white text-sm" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggleShow, testId, hint }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase mb-1.5 block">{label}</label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          autoComplete="new-password"
          className="pr-10 bg-tw-surface border-tw text-white h-11 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
        />
        <button
          type="button"
          onClick={onToggleShow}
          data-testid={`${testId}-toggle`}
          aria-label={show ? "Verberg wachtwoord" : "Toon wachtwoord"}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-tw-muted hover:text-tw-yellow"
        >
          {show ? <EyeOff size={15} strokeWidth={2.5} /> : <Eye size={15} strokeWidth={2.5} />}
        </button>
      </div>
      {hint && <p className="mt-1.5 text-[11px] text-tw-muted">{hint}</p>}
    </div>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== repeat) {
      toast.error("Wachtwoorden komen niet overeen.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/me/password", { current_password: current, new_password: next });
      if (data?.access_token) localStorage.setItem("tw1_token", data.access_token);
      toast.success("Wachtwoord gewijzigd.");
      setCurrent("");
      setNext("");
      setRepeat("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 sm:px-8 py-6 sm:py-14" data-testid="account-page">
      <header className="mb-8 pb-6 border-b border-tw">
        <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">Persoonlijk</p>
        <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black text-white leading-none tracking-tight">
          Mijn <span className="text-tw-yellow">account.</span>
        </h1>
      </header>

      <section className="mb-10 space-y-4" data-testid="account-profile">
        <ProfileField label="Naam" value={user.name} testId="account-name" />
        <ProfileField label="E-mail" value={user.email} testId="account-email" />
        <ProfileField label="Functie" value={user.title} testId="account-title" />
        <div>
          <ProfileField label="Locatie" testId="account-location"
            value={ownLocations(user).map((l, i) => (i === 0 && ownLocations(user).length > 1 ? `${l} (standaard)` : l)).join(", ")} />
          <p className="mt-1 text-xs text-tw-muted leading-relaxed" data-testid="account-location-hint">
            {ownLocations(user).length
              ? "Je memo's krijgen de locatie die je noemt, anders je standaardlocatie. Alleen een beheerder kan je locaties wijzigen."
              : "Geen vaste vestiging — dat is normaal als je over alle locaties werkt. Alleen een beheerder kan dit wijzigen."}
          </p>
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-tw-muted uppercase mb-1">Rol</div>
          <div className="text-white text-sm" data-testid="account-role">{ROLE_LABELS[user.role] || user.role}</div>
          <p className="mt-1 text-xs text-tw-muted leading-relaxed" data-testid="account-role-hint">
            {ROLE_HINTS[user.role]}
          </p>
          {user.role === "admin" && (
            <p className="mt-3 text-xs text-tw-muted leading-relaxed" data-testid="account-admin-password-note">
              Let op: na een wachtwoordwijziging hier is het ADMIN_PASSWORD uit de omgeving niet
              meer leidend — herstel kan alleen via ADMIN_PASSWORD_FORCE_RESET=1 bij een redeploy.
            </p>
          )}
        </div>
      </section>

      <section className="pt-6 border-t border-tw" data-testid="account-password-section">
        <div className="flex items-center gap-2 mb-5">
          <KeyRound size={14} className="text-tw-yellow" strokeWidth={2.5} />
          <h2 className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase">Wachtwoord wijzigen</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-password-form">
          <PasswordField
            label="Huidig wachtwoord"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((v) => !v)}
            testId="account-current-password"
          />
          <PasswordField
            label="Nieuw wachtwoord"
            value={next}
            onChange={setNext}
            show={showNext}
            onToggleShow={() => setShowNext((v) => !v)}
            testId="account-new-password"
            hint="Minimaal 10 tekens"
          />
          <PasswordField
            label="Herhaal nieuw wachtwoord"
            value={repeat}
            onChange={setRepeat}
            show={showNext}
            onToggleShow={() => setShowNext((v) => !v)}
            testId="account-repeat-password"
          />
          <Button
            type="submit"
            disabled={busy || !current || !next || !repeat}
            data-testid="account-password-submit"
            className="tw-press w-full h-12 rounded-none bg-tw-yellow text-black hover:bg-[#FFDE33] uppercase tracking-[0.2em] text-sm font-bold disabled:opacity-50"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Wachtwoord opslaan"}
          </Button>
        </form>
      </section>
    </div>
  );
}
